import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import __DEPRECATED_defineResources from 'dpl/shared/fetching/deprecated/defineResources';
import {
  LITTER_ID,
  PUPPY_ID,
  BREED_ID,
  DOG_ID
} from 'dpl/constants/query_params';
import { LITTER_STATES } from 'dpl/shared/constants/shared';
import { CONSTRAINT_TYPES } from 'dpl/shared/validations/constraints';
import {
  breederProfileAvailabilityResourceDefinition,
  breederProfileDogResourceDefinition,
  breederProfilePuppiesResourceDefinition,
  littersResourceDefinition,
  singleBreederProfileResourceDefinition
} from 'dpl/shared/utils/tptConnectResourceDefinitions';
import { buyerProfileEditResourceDefinition } from 'dpl/shared/buyer/utils/queryDefinitions';
import { flatten, uniqBy } from 'dpl/shared/utils';
import { nameUnnamedPuppies } from 'dpl/shared/utils/breeder_app';
import withCurrentUser from 'dpl/decorators/withCurrentUser';
import withFormCapabilities from 'dpl/form/decorators/withFormCapabilities';
import withUploadCapabilities from 'dpl/decorators/withUploadCapabilities';
import ApplicationLayout from 'dpl/adoption_application/components/ApplicationLayout';
import { get } from 'dpl/shared/utils/object';
import { sendAnalyticsIdentify } from 'dpl/util/analytics';
import { DELIVERY_METHOD_KEYS } from 'dpl/adoption_application/utils/constants';

import flightNannyImg from 'dpl/assets/dm_flight_nanny.jpg';
import pickupAirportImg from 'dpl/assets/dm_pickup_airport.jpg';
import pickupImg from 'dpl/assets/dm_pickup_local.jpg';
import groundTransportationImg from 'dpl/assets/dm_ground_transportation.jpg';
import airCargoImg from 'dpl/assets/dm_air_cargo.jpg';
import meetHalfwayImg from 'dpl/assets/dm_meet_halfway.jpg';

function ApplicationLayoutContainer(props) {
  return <ApplicationLayout {...props} />;
}

export default compose(
  withCurrentUser,
  connect(({ queryParams }) => ({
    litterId: Number(queryParams[LITTER_ID]),
    parentId: Number(queryParams[DOG_ID]),
    puppyId: Number(queryParams[PUPPY_ID]),
    breedId: Number(queryParams[BREED_ID])
  })),
  __DEPRECATED_defineResources(
    ({ server }, { isLoggedIn, currentUser, breedId, parentId }) => {
      const baseUrl = `/api/breeder_profiles/${server.breederProfileId}/buyer_applications`;

      return {
        breederProfileData: singleBreederProfileResourceDefinition(
          server.breederProfileId
        ),
        buyerProfileData: {
          ...buyerProfileEditResourceDefinition(
            currentUser.value.data?.buyer_profile_id
          ),
          auto: Boolean(isLoggedIn)
        },
        littersData: littersResourceDefinition(server.breederProfileId),
        parentData: {
          ...breederProfileDogResourceDefinition(
            server.breederProfileId,
            parentId
          ),
          auto: Boolean(parentId)
        },
        puppiesData: breederProfilePuppiesResourceDefinition(
          server.breederProfileId
        ),
        availabilityData: breederProfileAvailabilityResourceDefinition(
          server.breederProfileId
        ),
        buyerApplicationData: {
          url: `${baseUrl}/new_or_edit.json`,
          params: { [BREED_ID]: breedId || null },
          defaultValue: {
            buyer_application: {
              buyer_profile_attributes: {
                address_attributes: {},
                user_attributes: {
                  profile_photo_attributes: {}
                }
              },
              children_ages: [],
              current_pets_types: [],
              custom_application_answers_attributes: [],
              delivery_method_preferences: []
            },
            available_values: {
              learning_center_pages: [],
              delivery_methods: {
                methods: []
              },
              breeds: []
            }
          },
          actions: {
            update: json => ({
              method: 'PATCH',
              url: `${baseUrl}/${json.id}.json`,
              body: { buyer_application: json }
              // NOTE: manually refetching so we can explicitly wait to transition to the next step
              // refetchAfter: 'success'
            }),
            create: json => ({
              method: 'POST',
              url: `${baseUrl}.json`,
              body: { buyer_application: json }
              // NOTE: manually refetching so we can explicitly wait to transition to the next step
              // refetchAfter: 'success'
            })
          }
        }
      };
    }
  ),
  connect(
    (
      state,
      {
        availabilityData,
        breederProfileData,
        littersData,
        parentData,
        puppiesData,
        litterId,
        puppyId,
        buyerApplicationData,
        buyerProfileData,
        isLoggedIn
      }
    ) => {
      const availableLitters = littersData.value.data.filter(
        l =>
          !l.reserved &&
          !l.snoozed &&
          [
            LITTER_STATES.BORN,
            LITTER_STATES.EXPECTED,
            LITTER_STATES.PLANNED
          ].includes(l.status)
      );
      const reservedLitters = littersData.value.data.filter(
        l =>
          l.reserved &&
          !l.snoozed &&
          [
            LITTER_STATES.BORN,
            LITTER_STATES.EXPECTED,
            LITTER_STATES.PLANNED
          ].includes(l.status)
      );
      const bornPuppies = puppiesData.value.data;
      const litterPuppies = flatten(availableLitters.map(l => l.puppies));

      const allPuppies = uniqBy(bornPuppies.concat(litterPuppies), 'id');
      const availablePuppies = nameUnnamedPuppies(
        allPuppies.filter(p => !p.reserved && !p.snoozed)
      );

      const {
        breeds,
        delivery_methods: deliveryMethods,
        learning_center_pages: learningCenterPages,
        adoptable_breed_name: adoptableBreedName
      } = buyerApplicationData.value.available_values;

      const breedsWithLitters = availableLitters.map(l => l.breed_id);
      const reservedBreedsWithLitters = reservedLitters.map(l => l.breed_id);

      let litterApplyingTo =
        littersData.value.data.find(l => l.id === litterId) || {};

      if (availableLitters.length === 1) {
        [litterApplyingTo] = availableLitters;
      }

      const parentApplyingTo = parentData?.value?.data || {};

      const availableBreeds = breeds.filter(
        b => !b.snoozed && breedsWithLitters.includes(b.id)
      );

      const reservedBreeds = breeds.filter(
        b => !b.snoozed && reservedBreedsWithLitters.includes(b.id)
      );

      const canOverrideAvailability =
        availabilityData.value.data.can_override_availability;

      const availableDeliveryMethods = {
        methods: deliveryMethods.methods,
        pickup: {
          ...deliveryMethods.pickup,
          photo_url: pickupImg
        }
      };

      if (deliveryMethods.airport_pickup) {
        availableDeliveryMethods.pickupAtAirport = {
          ...deliveryMethods.airport_pickup,
          photo_url: pickupAirportImg
        };
      }

      if (availableDeliveryMethods.methods.length) {
        availableDeliveryMethods.methods.forEach(dm => {
          if (dm.key === DELIVERY_METHOD_KEYS.AIR_CARGO) {
            dm.photo_url = airCargoImg;
          } else if (dm.key === DELIVERY_METHOD_KEYS.GROUND_TRANSPORT) {
            dm.photo_url = groundTransportationImg;
          } else if (dm.key === DELIVERY_METHOD_KEYS.MEET_HALFWAY) {
            dm.photo_url = meetHalfwayImg;
          } else if (dm.key === DELIVERY_METHOD_KEYS.FLIGHT_NANNY) {
            dm.photo_url = flightNannyImg;
          }
        });
      }

      const isDataLoaded =
        littersData.meta.isSuccess &&
        breederProfileData.meta.isSuccess &&
        buyerApplicationData.meta.isSuccess &&
        !buyerApplicationData.meta.isFetching &&
        !availabilityData.meta.isFetching &&
        (!buyerProfileData.meta.isFetching || isLoggedIn === false);

      return {
        isLoadingData: !isDataLoaded,
        puppyApplyingTo: allPuppies.find(p => p.id === puppyId) || {},
        litterApplyingTo,
        parentApplyingTo,
        availableBreeds,
        availableLitters,
        availablePuppies,
        reservedBreeds,
        reservedLitters,
        canOverrideAvailability,
        learningCenterPages,
        availableDeliveryMethods,
        adoptableBreedName
      };
    }
  ),
  withUploadCapabilities,
  withFormCapabilities(
    ({
      availableBreeds,
      reservedBreeds,
      canOverrideAvailability,
      availableDeliveryMethods,
      buyerApplicationData,
      litterApplyingTo,
      puppyApplyingTo,
      parentApplyingTo,
      isSubmittingAndRefetching
    }) => {
      if (!buyerApplicationData.meta.isSuccess || isSubmittingAndRefetching) {
        return null;
      }

      const { buyer_application: buyerApplication } =
        buyerApplicationData.value;

      // We want to be careful to only use breed_id from parent if they
      // don't simultaneously have purebreed and crossbreed puppies
      const parentBreedId =
        parentApplyingTo?.active_litter_breed_ids?.length === 1
          ? parentApplyingTo.active_litter_breed_ids[0]
          : null;
      let breedId =
        litterApplyingTo.breed_id ||
        puppyApplyingTo.breed_id ||
        parentBreedId ||
        buyerApplication.breed_id;

      if (!breedId && availableBreeds.length === 1) {
        breedId = availableBreeds[0].id;
      } else if (
        !breedId &&
        reservedBreeds.length === 1 &&
        canOverrideAvailability
      ) {
        breedId = reservedBreeds[0].id;
      }

      // defaults "can pickup" delivery option to true if it's the only option
      const pickupOnly =
        availableDeliveryMethods.pickup &&
        availableDeliveryMethods.methods.length === 0 &&
        !availableDeliveryMethods.pickupAtAirport;

      if (pickupOnly) {
        buyerApplication.can_pickup = true;
      }

      const baseApplication = {
        ...buyerApplication,
        breed_id: breedId
      };

      if (buyerApplication.id) {
        return baseApplication;
      }

      return {
        ...baseApplication,
        // initialize formState with the litter/puppy user navigated to
        litter_id: litterApplyingTo.id,
        dog_id: puppyApplyingTo.id
      };
    },
    {
      onSubmit: (
        { buyerApplicationData, onSubmit, onBeforeSubmit },
        formStateForSubmission,
        submit
      ) => {
        const submitFunc = formStateForSubmission.id
          ? buyerApplicationData.update
          : buyerApplicationData.create;

        if (submit && onBeforeSubmit) {
          onBeforeSubmit();
        }

        return submitFunc({ ...formStateForSubmission, submit }).then(
          response => {
            sendAnalyticsIdentify({
              userId: get(
                response,
                'data.buyer_application.buyer_profile_attributes.user_attributes.id'
              )
            });
            if (submit && onSubmit) {
              onSubmit();
            }
          }
        );
      },
      mapPropsAndStateToValidationSchema: (props, formState) => ({
        'buyer_profile_attributes.user_attributes': !formState
          .buyer_profile_attributes.user_attributes.id && {
          password: CONSTRAINT_TYPES.PASSWORD
        },
        'buyer_profile_attributes.user_attributes.email':
          // new user
          (!formState.buyer_profile_attributes.user_attributes.id ||
            // email changed
            formState.buyer_profile_attributes.user_attributes.email !==
              props.buyerApplicationData.value.buyer_application
                .buyer_profile_attributes.user_attributes.email) &&
          CONSTRAINT_TYPES.EMAIL_VALID,
        'buyer_profile_attributes.address_attributes.postal_code':
          CONSTRAINT_TYPES.ZIP,
        'buyer_profile_attributes.user_attributes.phone_number': formState
          .buyer_profile_attributes.user_attributes.phone_number
          ? CONSTRAINT_TYPES.TELEPHONE
          : CONSTRAINT_TYPES.TEXT
      }),
      associationFieldsToBeInitialized: [
        'custom_application_answers_attributes'
      ]
    }
  )
)(ApplicationLayoutContainer);
